import { Info, LinkIcon } from 'lucide-react'

import { CurrencyAmount } from '~/components/component-library'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/component-library/Dropdown'
import { TokenSelectorOption } from '~/components/smart-accounts/TokenSelectorOption'
import { PositionClaimDialog } from '~/components/smart-accounts/positions/PositionClaimDialog'
import { PositionDetailsDialog } from '~/components/smart-accounts/positions/PositionDetailsDialog'
import { PositionBalance } from '~/components/smart-accounts/positions/types'
import { getPositionTypeLabel } from '~/components/smart-accounts/positions/utils'
import { VestingClaimIcon } from '~/components/smart-accounts/vesting/VestingClaimIcon'
import { useAuth } from '~/context/AuthProvider'
import { copyToClipboard } from '~/utils/copyToClipboard'
import { useIsMobile } from '~/utils/devices'
import { captureException } from '~/utils/errors'

export const PositionBalanceRow = ({
  balance,
}: {
  balance: PositionBalance
}) => {
  const primaryToken = balance.tokens[0]

  if (!primaryToken) {
    captureException(new Error('Position missing primary token'), {
      extra: {
        balanceId: balance.id,
        chainId: balance.chainId,
      },
    })
    return null
  }

  const hasMultipleTokens = balance.tokens.length > 1
  const aggregateUsdValue = balance.feesUsdValue || balance.usdValue

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="rounded-lg data-[state=open]:bg-gray-100 dark:data-[state=open]:bg-gray-800">
          <div className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 transition hover:cursor-pointer md:hover:bg-gray-100 md:dark:hover:bg-gray-800">
            <div className="min-w-0 flex-1">
              <TokenSelectorOption
                token={primaryToken.address}
                chainId={balance.chainId}
                balance={primaryToken.claimableAmount}
                hideBalance={hasMultipleTokens}
                protocol={getPositionTypeLabel(balance.positionType)}
                className="!px-0 !py-0"
                suffix={
                  balance.tokens.length > 1 ? (
                    <span className="text-xs text-secondary">
                      +{balance.tokens.length - 1}
                    </span>
                  ) : undefined
                }
              />
            </div>
            {hasMultipleTokens && (
              <div className="flex-shrink-0 text-right text-sm text-primary">
                {aggregateUsdValue === null ? (
                  '--'
                ) : (
                  <CurrencyAmount layout="inline" value={aggregateUsdValue} />
                )}
              </div>
            )}
          </div>
        </div>
      </DropdownMenuTrigger>
      <PositionDropDownContent balance={balance} />
    </DropdownMenu>
  )
}

const PositionDropDownContent = ({ balance }: { balance: PositionBalance }) => {
  const isMobile = useIsMobile()
  const { setGlobalModalState } = useAuth()
  const hasMultipleTokens = balance.tokens.length > 1

  return (
    <DropdownMenuContent
      className="w-48"
      align={isMobile ? 'center' : 'start'}
      side={isMobile ? 'bottom' : 'left'}
    >
      <DropdownMenuItem
        onClick={() => {
          setGlobalModalState({
            isOpen: true,
            content: (
              <PositionClaimDialog
                positionBalance={balance}
                setIsOpen={() => {
                  setGlobalModalState({
                    isOpen: false,
                    content: null,
                  })
                }}
              />
            ),
          })
        }}
      >
        <VestingClaimIcon />
        <div>Claim</div>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onClick={() => {
          setGlobalModalState({
            isOpen: true,
            content: <PositionDetailsDialog balance={balance} />,
          })
        }}
      >
        <Info />
        <div>View details</div>
      </DropdownMenuItem>
      {!hasMultipleTokens && (
        <DropdownMenuItem
          onClick={() => {
            copyToClipboard(
              balance.tokens[0]?.address,
              'Token address copied to clipboard',
            )
          }}
        >
          <LinkIcon />
          <div>Copy address</div>
        </DropdownMenuItem>
      )}
    </DropdownMenuContent>
  )
}
